import { css, keyframes } from '@emotion/react';

import { COLORS2 } from '@/Client/Styles';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { MODAL_COLORS } from '@/Client/Widgets/Modal/ModalStyleLib';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ACCENT } from '../../scanVideoMediaStyles';

const RECORD_RED = COLORS2.alwaysDark.red;

const recPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const transcribePulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.85; }
`;

export const TIMELINE_TRACK_STYLE = css`
  label: VIDEO_TIMELINE_TRACK;
  position: relative;
  height: 44px;
  background: ${styleguideColors.surfaceMuted};
  border: 1px solid ${MODAL_COLORS.border};
  cursor: pointer;
  touch-action: none;
`;

export const timelineTrackDisabledStyle = css`
  cursor: not-allowed;
`;

export const timelinePlayheadStyle = (leftFraction: number) =>
  css`
  label: VIDEO_TIMELINE_PLAYHEAD;
  position: absolute;
  top: -4px;
  bottom: -4px;
  left: ${leftFraction * 100}%;
  width: 2px;
  margin-left: -1px;
  background: ${styleguideColors.ink};
  pointer-events: none;
`;

export const timelineRangeStyle = (leftFraction: number, widthFraction: number) =>
  css`
  label: VIDEO_TIMELINE_RANGE;
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${leftFraction * 100}%;
  width: ${widthFraction * 100}%;
  background: ${ACCENT};
`;

export const timelineActiveRecordingStyle = css`
  label: VIDEO_TIMELINE_RECORDING;
  background: ${RECORD_RED};
  animation: ${recPulse} 1.2s ease-in-out infinite;
`;

export const TRANSCRIBE_STATUS_ROW_STYLE = css`
  label: VIDEO_TRANSCRIBE_STATUS_ROW;
  position: relative;
  height: 18px;
  margin-bottom: 4px;
`;

export const transcribeStatusChipStyle = (leftFraction: number, widthFraction: number) =>
  css`
  label: VIDEO_TRANSCRIBE_STATUS_CHIP;
  position: absolute;
  top: 0;
  left: ${leftFraction * 100}%;
  width: ${Math.max(widthFraction, 0.02) * 100}%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  pointer-events: auto;
  cursor: help;
`;

export const transcribeStatusLabelStyle = (tone: 'running' | 'failed' | 'completed') =>
  css`
  label: VIDEO_TRANSCRIBE_STATUS_LABEL;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${
    tone === 'failed'
      ? RECORD_RED
      : tone === 'completed'
      ? COLORS2.alwaysDark.green
      : COLORS2.alwaysDark.blue
  };
`;

export const transcribeProgressTrackStyle = (tone: 'running' | 'failed' | 'completed') =>
  css`
  label: VIDEO_TRANSCRIBE_PROGRESS_TRACK;
  height: 3px;
  border-radius: 999px;
  background: ${MODAL_COLORS.border};
  overflow: hidden;
  cursor: help;
`;

export const transcribeProgressFillStyle = (
  percent: number,
  tone: 'running' | 'failed' | 'completed',
) =>
  css`
  label: VIDEO_TRANSCRIBE_PROGRESS_FILL;
  height: 100%;
  width: ${percent}%;
  border-radius: inherit;
  background: ${
    tone === 'failed'
      ? RECORD_RED
      : tone === 'completed'
      ? COLORS2.alwaysDark.green
      : COLORS2.alwaysDark.blue
  };
  animation: ${tone === 'running' ? transcribePulse : 'none'} 1.4s ease-in-out infinite;
`;

export const timelineTranscribeOverlayStyle = (tone: 'running' | 'failed' | 'completed') =>
  css`
  label: VIDEO_TIMELINE_TRANSCRIBE_OVERLAY;
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
  background: ${
    tone === 'failed'
      ? 'rgba(220, 38, 38, 0.22)'
      : tone === 'completed'
      ? 'rgba(34, 197, 94, 0.18)'
      : 'rgba(59, 130, 246, 0.22)'
  };
  border: 1px solid ${
    tone === 'failed'
      ? RECORD_RED
      : tone === 'completed'
      ? COLORS2.alwaysDark.green
      : COLORS2.alwaysDark.blue
  };
  animation: ${tone === 'running' ? transcribePulse : 'none'} 1.4s ease-in-out infinite;
`;
